import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { OnBoardingFacade } from '@api-ui-app/src/app/on-boarding/on-boarding.facade';
import { trackEvent } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { QuickQuestions, QuickQuestionsSteps } from '@gelato-api-ui/core/quick-questions/types/quick-questions';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { TitleService } from '../../../shared/services/title.service';

@Component({
  selector: 'gd-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.scss'],
})
export class QuestionnaireComponent implements OnInit, OnDestroy, AfterViewChecked {
  userId$ = this.facade.userId$;
  originalQuickQuestions$ = this.facade.originalQuickQuestions$;
  countries$ = this.facade.countries$;
  isLoading$ = this.facade.isLoading$;
  isSubmittingForm$ = this.facade.isSubmittingForm$;
  isTermsAccepted$ = this.facade.isTermsAccepted$;
  error$ = this.facade.error$;
  quickQuestions$ = this.facade.quickQuestions$;
  activeQuickQuestionStep$ = this.facade.activeQuickQuestionStep$;
  canProceedToFinish$ = this.facade.canProceedToFinish$;
  canProceed$ = this.facade.canProceed$;
  automaticallyGoToNextStep$ = this.facade.automaticallyGoToNextStep$;
  sectionHeight$ = new BehaviorSubject('100%');
  quickQuestionsHeight$ = new BehaviorSubject('100%');
  buttonsConfig$ = this.facade.buttonsConfig$;
  showNextBtn$ = this.buttonsConfig$.pipe(map(c => c.showNextBtn));
  showSkipBtn$ = this.buttonsConfig$.pipe(map(c => c.showSkipBtn));
  skipButtonTextKey$ = this.activeQuickQuestionStep$.pipe(
    map(activeQuestion => {
      if (activeQuestion === QuickQuestionsSteps.ecommercePlatform) {
        return 'txt_skip_i_do_not_have_one';
      }

      return 'txt_skip';
    }),
  );
  showBackBtn$ = this.facade.isFirstQuickQuestionStep$.pipe(map(f => !f));
  showButtons$ = combineLatest([
    this.showBackBtn$,
    this.showNextBtn$,
    this.showSkipBtn$,
    this.canProceedToFinish$,
  ]).pipe(map(([back, next, skip, finish]) => back || next || skip || finish));
  ngDestroy$ = new Subject();

  @ViewChild('termsAndConditions') termsAndConditions: ElementRef;
  @ViewChild('buttons') buttons: ElementRef;

  constructor(private titleService: TitleService, private facade: OnBoardingFacade, private cdRef: ChangeDetectorRef) {}

  ngOnInit() {
    this.titleService.setTitle('{txt_meta_title_questionnaire}');

    this.userId$
      .pipe(
        takeUntil(this.ngDestroy$),
        filter((userId: string): boolean => Boolean(userId)),
        tap((userId: string) => {
          trackEvent({
            event: 'setUserId',
            userId,
          });
        }),
      )
      .subscribe();

    this.originalQuickQuestions$
      .pipe(
        takeUntil(this.ngDestroy$),
        tap((quickQuestions: QuickQuestions) => {
          this.facade.setQuickQuestions(quickQuestions);
        }),
      )
      .subscribe();

    this.facade.init();
  }

  ngAfterViewChecked() {
    this.updateSectionHeight();
    this.updateQuickQuestionsHeight();
  }

  updateQuickQuestionsHeight() {
    const termsHeight = this.termsAndConditions?.nativeElement.clientHeight || 0;
    const gap = termsHeight ? '14' : '0';
    const value = `calc(100% - ${gap}px - ${termsHeight}px)`;
    if (value !== this.quickQuestionsHeight$.value) {
      this.quickQuestionsHeight$.next(value);
      this.cdRef.detectChanges();
    }
  }

  updateSectionHeight() {
    const buttonsHeight = this.buttons?.nativeElement.clientHeight || 0;
    const value = `calc(100% - ${buttonsHeight}px)`;
    if (value !== this.sectionHeight$.value) {
      this.sectionHeight$.next(value);
      this.cdRef.detectChanges();
    }
  }

  hasError(fieldName: string): Observable<boolean> {
    return this.facade.hasError(fieldName);
  }

  onQuickQuestionsChange(quickQuestions: QuickQuestions): void {
    this.facade.setQuickQuestions(quickQuestions);

    this.automaticallyGoToNextStep$
      .pipe(
        take(1),
        filter(value => Boolean(value)),
      )
      .subscribe(() => this.goToNextQuickQuestion());
  }

  onClickTerms(termsAndConditions: boolean) {
    this.facade.setTermsAndConditions(termsAndConditions);
  }

  onClickReceiveNews(receiveNews: boolean) {
    this.facade.setReceiveNews(receiveNews);
  }

  goToPreviousQuickQuestion() {
    this.facade.goToPreviousQuickQuestion();
  }

  onSkipQuestion() {
    this.activeQuickQuestionStep$
      .pipe(
        take(1),
        tap((activeQuickQuestionStep: QuickQuestionsSteps) => {
          if (activeQuickQuestionStep === QuickQuestionsSteps.ecommercePlatform) {
            this.goToNextQuickQuestion('skip_i_do_not_have_one');
            return;
          }

          this.goToNextQuickQuestion('skip');
        }),
      )
      .subscribe();
  }

  goToNextQuickQuestion(answer?: string) {
    this.facade.goToNextQuickQuestion(answer);
  }

  submit() {
    this.facade.submitQuickQuestions();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next(true);
    this.ngDestroy$.complete();
  }
}
